/**
 * Purpose:
 *   Computes button styles based on variant, size, and state.
 */

import { cx } from '@/util/functions/cx';
import { buttonStyles } from '../Button.styles';
import type { TButtonVariant, TButtonSize } from '../Button.types';

export interface IUseButtonStylesProps {
  variant: TButtonVariant;
  size: TButtonSize;
  iconOnly: boolean;
  disabled: boolean;
  /** When true, apply hover appearance (for demo/Storybook). Same classes as :hover. */
  forceHover?: boolean;
  /** When true, apply focus-visible ring (for demo/Storybook). Same as focus-visible in base. */
  forceFocusVisible?: boolean;
}

export function useButtonStyles({
  variant,
  size,
  iconOnly,
  disabled,
  forceHover = false,
  forceFocusVisible = false,
}: IUseButtonStylesProps) {
  const sizeClasses = size === 'sm' ? buttonStyles.sizeSm : buttonStyles.sizeMd;

  const withPadding =
    !iconOnly && variant !== 'tertiary'
      ? size === 'sm'
        ? buttonStyles.paddingSm
        : buttonStyles.paddingMd
      : '';

  const variantClasses = disabled
    ? buttonStyles.variants[variant].disabled
    : buttonStyles.variants[variant].default;

  const hoverAppearance =
    forceHover && !disabled
      ? buttonStyles.variants[variant].hoverAppearance
      : '';

  const focusVisibleAppearance = forceFocusVisible
    ? buttonStyles.focusVisibleAppearance[variant]
    : '';

  const iconOnlyClasses = iconOnly
    ? size === 'sm'
      ? buttonStyles.iconOnly.sm
      : buttonStyles.iconOnly.md
    : '';

  return {
    className: cx(
      sizeClasses,
      withPadding,
      variantClasses,
      buttonStyles.focusVisible[variant],
      hoverAppearance,
      focusVisibleAppearance,
      iconOnlyClasses
    ),
  };
}

